"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.flushDispatchQueue = exports.findAny = exports.dispatch = exports.docData = exports.rootData = exports.doc = exports.root = exports.findTag = exports.findAll = exports.find = exports.register = exports.removeAppRegisteredListener = exports.addAppRegisteredListener = exports.registry = void 0;
const oneaudi_os_utils_1 = require("@oneaudi/oneaudi-os-utils");
const FeatureAppModel_1 = require("./FeatureAppModel");
const registry_1 = require("./registry");
var registry_2 = require("./registry");
Object.defineProperty(exports, "registry", { enumerable: true, get: function () { return registry_2.registry; } });
/**
 * Listeners for pending app registrations
 * Register a listener when the app you're trying to find() hasn't been registered yet
 * and you'd like to be notified when it has.
 */
const appListeners = new Map();
/**
 * Register a listener for app registration.
 * Each app will only be registered once. Once the listener has been called,
 * it will be removed automatically.
 * Duplicate listeners (by reference) are called only once.
 */
function addAppRegisteredListener(identifier, listener) {
    if (!appListeners.has(identifier)) {
        appListeners.set(identifier, new Set());
    }
    appListeners.get(identifier).add(listener);
}
exports.addAppRegisteredListener = addAppRegisteredListener;
/**
 * Remove a listener for app registration.
 * Once a listener has been called, it will be removed automatically.
 */
function removeAppRegisteredListener(identifier, listener) {
    appListeners.get(identifier)?.delete(listener);
}
exports.removeAppRegisteredListener = removeAppRegisteredListener;
function flushAppListeners(app) {
    for (const identifier of [
        app.id,
        app.name,
        ...Object.values(app.meta || {}),
    ]) {
        appListeners.get(identifier)?.forEach((cb) => cb(app));
        appListeners.delete(identifier);
    }
}
/**
 * Register a Feature App instance
 * @param id Feature App identifier. Null for the root/page.
 * @param values Feature App metadata
 */
function register(id, values) {
    const app = new FeatureAppModel_1.FeatureAppModel(values);
    registry_1.registry.set(id, app);
    if ((0, oneaudi_os_utils_1.isSSR)()) {
        // save state for hydration
        app.subscribe((newData) => (0, oneaudi_os_utils_1.storeForHydration)(id, newData));
    }
    // go through the app listeners and dispatch queue after this sync operation batch is completed
    setTimeout(() => {
        flushAppListeners(app);
        flushDispatchQueue();
    }, 0);
    return app;
}
exports.register = register;
/**
 * Find an instantiated Feature App from the registry.
 * @param identifier ID or Route of the app to find
 */
function find(identifier) {
    if (registry_1.registry.has(identifier)) {
        return registry_1.registry.get(identifier);
    }
    for (const app of registry_1.registry.values()) {
        if (app &&
            (app.id === identifier ||
                app.name === identifier ||
                Object.values(app.meta || {}).some((value) => value === identifier))) {
            return app;
        }
    }
    return undefined;
}
exports.find = find;
/**
 * Find All apps that match the query.
 * @param identifier ID or Route of the app to find
 */
function findAll(query) {
    return Array.from(registry_1.registry, ([id, app]) => 
    // check if id matches
    query.id === id ||
        query.id === app.id ||
        query.id === app.name ||
        // check if any property matches one on one
        Object.keys(query).some((key) => app[key] === query[key]) ||
        // check if any meta property matches one on one
        Object.keys(query).some((key) => app.meta[key] === query[key]) ||
        // tags are matching any of the tag
        app.tags?.some?.((appTag) => query?.tags?.includes?.(appTag)) ||
        // tags are matching the only provided tag in query
        (typeof query.tag === 'string' && app.tags?.includes?.(String(query.tag)))
        ? app
        : undefined).filter(Boolean);
}
exports.findAll = findAll;
/**
 * Find all Feature Apps with a certain tag
 * @param tag Tag
 */
function findTag(tag) {
    if (tag === '*') {
        // wildcard for all entries
        return Array.from(registry_1.registry.values());
    }
    return Array.from(registry_1.registry)
        .filter(([, app]) => app.tags?.includes(tag))
        .map(([, app]) => app);
}
exports.findTag = findTag;
function root() {
    return find('root');
}
exports.root = root;
/**
 * @deprecated Please use root() instead
 */
exports.doc = root;
function rootData() {
    return root()?.data || {};
}
exports.rootData = rootData;
/**
 * @deprecated Please use rootData() instead
 */
exports.docData = rootData;
/**
 * Dispatcher emits actions and payloads to a feature app that matches the identifier
 * Example
 *    dispatch<string>('root', 'changeTitle', title).then(([res]) =>
 *      console.log('action responded with', res?.response),
 *    );
 *
 * @param identifier
 * @param actionName
 * @param args
 */
function dispatch(identifier, actionName, ...args) {
    const dispatchPromise = new Promise((resolve, reject) => {
        registry_1.dispatchQueue.set([resolve, reject], [identifier, actionName, ...args]);
    });
    flushDispatchQueue();
    return dispatchPromise;
}
exports.dispatch = dispatch;
/**
 * Smart find by a string that can match tags or query or string
 * @param identifier
 */
function findAny(identifier) {
    let instances = [];
    if (typeof identifier === 'string') {
        // searching for IDs
        instances = [find(identifier)].filter(Boolean);
        if (!instances.length) {
            // If we haven't found anything so far, let's try to search for tags
            instances = findTag(identifier);
        }
    }
    else if (typeof identifier === 'object') {
        // search by all query fields if Query object is supplied
        instances = findAll(identifier);
    }
    if (!instances?.length) {
        return [];
    }
    // Dispatch to all found instances
    return instances.filter(Boolean);
}
exports.findAny = findAny;
function flushDispatchQueue() {
    Array.from(registry_1.dispatchQueue, ([key, [identifier, actionName, ...args]]) => {
        const [resolve] = key;
        const instances = findAny(identifier);
        if (instances && instances.length > 0) {
            const results = instances
                .map((instance) => {
                const dispatchResult = instance.dispatch(actionName, ...args);
                // Something went wrong. Let's not flush this one.
                if (!dispatchResult?.success)
                    return false;
                return {
                    response: dispatchResult?.response,
                    app: instance,
                };
            })
                // filter out any failed executions
                .filter(Boolean);
            // if there are at least some successful executions then move on
            if (results.length > 0) {
                registry_1.dispatchQueue.delete(key);
                resolve(results);
            }
        }
    });
}
exports.flushDispatchQueue = flushDispatchQueue;
if (process.env.NODE_ENV === 'development') {
    // expose variables for development purposes
    globalThis.registry = registry_1.registry;
    globalThis.dispatch = dispatch;
    globalThis.flushDispatchQueue = flushDispatchQueue;
    globalThis.dispatchQueue = registry_1.dispatchQueue;
}
